// deps
import React, { memo, useCallback, useContext, useRef } from "react";

// internal-deps
import {
    IrctcAddressChangeHalfCard,
    IrctcAddressChangeFormData,
} from "@swiggy-private/dls-web-recipe-common-web/dist/components/IrctcAddressChange";
import { IrctcAddressChangeFormKeys, IIrctcPayload } from "@swiggy-private/cart-types";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// helpers
import { getIrctcIdentifier } from "@cart-client/helpers/Cart";

// hooks
import { useIrctcAnalytics } from "@cart-client/hooks/useIrctcAnalytics";
import { useKeyboardState } from "@swiggy-private/checkout-core";

interface IrctcAddressChangeWrapperProps {
    isOpen: boolean;
    ctaType?: IrctcAddressChangeFormKeys;
    setIrctcAddressChange: (irctcAddressChange: { isOpen: boolean; ctaType?: IrctcAddressChangeFormKeys }) => void;
}

const IrctcAddressChangeWrapper = ({ isOpen, ctaType, setIrctcAddressChange }: IrctcAddressChangeWrapperProps) => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse, localCart } = cartState;
    const { doUpdateCart } = useContext(UpdateCartContext);
    const irctcHalfCardRef = useRef<HTMLDivElement>(null);
    useKeyboardState(irctcHalfCardRef);
    const { onImpressionIrctcAddressChange, onClickIrctcAddressChange } = useIrctcAnalytics(cartResponse);

    const closeIrctcAddressChange = useCallback(() => {
        setIrctcAddressChange({ isOpen: false });
    }, [setIrctcAddressChange]);

    const onSubmitAddressChange = useCallback(
        (formData: IrctcAddressChangeFormData) => {
            doUpdateCart({
                ...localCart,
                /* eslint-disable @typescript-eslint/naming-convention */
                irctc_data: {
                    ...localCart?.irctc_data,
                    coach: formData.coachNo,
                    berth: formData.seatNo,
                    mobile_number: formData.contactNo,
                } as IIrctcPayload,
                /* eslint-enable @typescript-eslint/naming-convention */
            });
            onClickIrctcAddressChange();
            closeIrctcAddressChange();
        },
        [doUpdateCart, localCart, closeIrctcAddressChange, onClickIrctcAddressChange],
    );

    if (!isOpen) {
        return null;
    }

    const irctcData = getIrctcIdentifier(cartResponse);

    return (
        <IrctcAddressChangeHalfCard
            isOpen={isOpen}
            ctaType={ctaType}
            formData={{
                coachNo: irctcData?.coach,
                seatNo: irctcData?.berth,
                contactNo: irctcData?.mobile_number,
            }}
            onSubmit={onSubmitAddressChange}
            onClose={closeIrctcAddressChange}
            onLoad={onImpressionIrctcAddressChange}
            irctcAddressChangeRef={irctcHalfCardRef}
        />
    );
};

export default memo(IrctcAddressChangeWrapper);
